import { extend } from "@dhx/ts-common/core";
import { create, el, inject } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { View } from "@dhx/ts-common/view";
import { Timepicker, TimepickerEvents } from "@dhx/ts-timepicker";
import { DateHelper } from "./DateHelper";
import { getFormatedDate } from "./DateFormatter";
import { linkButtonClasses } from "./helper";
import locale from "./locales/en";
import { CalendarEvents, ICalendar, ICalendarConfig, ICalendarWeek, ViewMode, ICalendarHandlersMap } from "./types";
import { IHandlers } from "@dhx/ts-common/types";


export class Calendar extends View implements ICalendar {
	public events: IEventSystem<CalendarEvents, ICalendarHandlersMap>;
	public config: ICalendarConfig;

	private _selected: Date[];
	private _currentDate: Date;
	private _currentViewMode: ViewMode;

	private _handlers: IHandlers;

	private _timepicker: Timepicker;
	private _time: string;
	private _linkedCalendar: Calendar;

	constructor(container: HTMLElement | string, config: ICalendarConfig = {}) {
		super(container, extend({
			weekStart: "sunday",
			thisMonthOnly: false,
			dateFormat: (window && (window as any).dhx && (window as any).dhx.dateFormat),
			width: "250px"
		}, config));

		this.events = new EventSystem<CalendarEvents>();
		this._selected = [];

		if (!this.config.dateFormat) {
			if (this.config.timePicker) {
				if (this.config.timeFormat === 12) {
					this.config.dateFormat = "%d/%m/%y %h:%i %A";
				} else {
					this.config.dateFormat = "%d/%m/%y %H:%i";
				}
			} else {
				this.config.dateFormat = "%d/%m/%y";
			}
		}
		if (this.config.value) {
			this._setSelected(this.config.value);
		}
		if (this.config.date) {
			this._currentDate = DateHelper.toDateObject(this.config.date, this.config.dateFormat);
		} else if (this._getSelected()) {
			this._currentDate = DateHelper.copy(this._getSelected());
		} else {
			this._currentDate = new Date();
		}
		switch (this.config.view) {
			case ViewMode.months:
				this._currentViewMode = ViewMode.months;
				break;
			case ViewMode.years:
				this._currentViewMode = ViewMode.years;
				break;
			default:
				this._currentViewMode = ViewMode.calendar;
		}

		this._initHandlers();

		if (this.config.timePicker) {
			this._timepicker = new Timepicker(null, { timeFormat: this.config.timeFormat, actions: true });
			const initTime = this._getSelected() || new Date();
			this._timepicker.setValue(initTime);
			this._time = this._timepicker.getValue();

			this._timepicker.events.on(TimepickerEvents.close, () => {
				this._timepicker.setValue(this._time);
				this.showDate(null, ViewMode.calendar);
			});

			this._timepicker.events.on(TimepickerEvents.save, () => {
				const { hour, minute, AM } = this._timepicker.getValue(true);
				const oldDate = this._getSelected();
				const newDate = DateHelper.withHoursAndMinutes(this._getSelected() || new Date(), hour, minute, AM);
				if (this.events.fire(CalendarEvents.beforeChange, [newDate, oldDate, true])) {
					this._selected[this._selected.length - 1] = newDate;
					this.events.fire(CalendarEvents.change, [newDate, oldDate, true]);
				}
				this._time = this._timepicker.getValue();
				this.showDate(null, ViewMode.calendar);
			});
		}

		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	setValue(value: Date | Date[] | string | string[]): boolean {
		if (!value || value instanceof Array && value.length === 0) {
			return false;
		}
		this._selected = [];
		const currentDate = value instanceof Array ? value[0] : value;
		const date = DateHelper.toDateObject(currentDate, this.config.dateFormat);
		const oldDate = DateHelper.copy(this._getSelected());

		if (!this.events.fire(CalendarEvents.beforeChange, [date, oldDate, false])) {
			return false;
		}

		this._setSelected(value);

		if (this._timepicker) {
			this._timepicker.setValue(date);
			this._time = this._timepicker.getValue();
		}

		this.showDate(this._getSelected());
		this.events.fire(CalendarEvents.change, [date, oldDate, false]);
		this.paint();
		return true;
	}
	getValue<T extends boolean = false>(asDateObject?: T): any {
		if (!this._selected[0]) {
			return null;
		}
		if (this.config.range) {
			return asDateObject
				? this._selected.map(date => DateHelper.copy(date))
				: this._selected.map(date => getFormatedDate(this.config.dateFormat, date));
		}
		return asDateObject
			? DateHelper.copy(this._selected[0])
			: getFormatedDate(this.config.dateFormat, this._selected[0]);
	}
	getCurrentMode(): ViewMode {
		return this._currentViewMode;
	}
	showDate(date?: Date, mode?: ViewMode) {
		if (date) {
			this._currentDate = DateHelper.copy(date);
		}
		if (mode) {
			this._currentViewMode = mode;
		}
		this.paint();
	}
	destructor() {
		if (this._linkedCalendar) {
			this._unlink();
		}
		if (this._timepicker) {
			this._timepicker.destructor();
		}
		this.unmount();
	}
	link(targetCalendar: Calendar) {
		if (this._linkedCalendar) {
			this._unlink();
		}

		this._linkedCalendar = targetCalendar;

		const rawLowerDate = this.getValue(true);
		const rawUpperDate = targetCalendar.getValue(true);

		let lowerDate: Date = rawLowerDate && DateHelper.dayStart(rawLowerDate);
		let upperDate: Date = rawUpperDate && DateHelper.dayStart(rawUpperDate);

		const rangeMark = (date: Date) => {
			if (lowerDate && upperDate) {
				return date >= lowerDate && date <= upperDate && getRangeClass(date);
			}
		};
		const getRangeClass = (date: Date) => {
			if (DateHelper.isSameDay(upperDate, lowerDate)) {
				return null;
			}
			let positionInRange = "dhx_calendar-day--in-range";
			if (DateHelper.isSameDay(date, lowerDate)) {
				positionInRange += " dhx_calendar-day--first-date";
			}
			if (DateHelper.isSameDay(date, upperDate)) {
				positionInRange += " dhx_calendar-day--last-date";
			}
			return positionInRange;
		};
		if (!this.config.$rangeMark || !this._linkedCalendar.config.$rangeMark) {
			this.config.$rangeMark = this._linkedCalendar.config.$rangeMark = rangeMark;
		}
		if (!this.config.block || !this._linkedCalendar.config.block) {
			this.config.block = (date: Date) => {
				if (upperDate) {
					return date > upperDate;
				}
			};
			this._linkedCalendar.config.block = (date: Date) => {
				if (lowerDate) {
					return date < lowerDate;
				}
			};
		}
		this.config.thisMonthOnly = true;
		targetCalendar.config.thisMonthOnly = true;

		this.events.on(CalendarEvents.change, date => {
			lowerDate = DateHelper.dayStart(date);
			this._linkedCalendar.paint();
		}, "link");

		this._linkedCalendar.events.on(CalendarEvents.change, date => {
			upperDate = DateHelper.dayStart(date);
			this.paint();
		}, "link");

		this._linkedCalendar.paint();
		this.paint();
	}
	private _unlink() {
		if (this._linkedCalendar) {
			this.config.$rangeMark = this._linkedCalendar.config.$rangeMark = null;
			this.config.block = this._linkedCalendar.config.block = null;

			this.events.detach(CalendarEvents.change, "link");
			this._linkedCalendar.events.detach(CalendarEvents.change, "link");
			this._linkedCalendar.paint();
			this.paint();

			this._linkedCalendar = null;
		}
	}
	private _setSelected(value: Date | Date[] | string | string[]): void {
		const currentDate = value instanceof Array ? value[0] : value;
		const date = DateHelper.toDateObject(currentDate, this.config.dateFormat);

		if (value instanceof Array && this.config.range) {
			const filterDate: Date[] = [];
			value.forEach((element, index) => {
				if (index < 2) {
					filterDate.push(DateHelper.toDateObject(element, this.config.dateFormat));
				}
			});
			if (filterDate.length === 2 && filterDate[0] < filterDate[1]) {
				filterDate.forEach(element => this._selected.push(element));
			} else {
				this._selected[0] = filterDate[0];
			}
		} else {
			this._selected[0] = date;
		}
	}
	private _getSelected(): Date {
		return this._selected[this._selected.length - 1];
	}
	private _draw() {
		switch (this._currentViewMode) {
			case ViewMode.calendar:
				this.events.fire(CalendarEvents.modeChange, [ViewMode.calendar]);
				return this._drawCalendar();
			case ViewMode.months:
				this.events.fire(CalendarEvents.modeChange, [ViewMode.months]);
				return this._drawMonthSelector();
			case ViewMode.years:
				this.events.fire(CalendarEvents.modeChange, [ViewMode.years]);
				return this._drawYearSelector();
			case ViewMode.timepicker:
				this.events.fire(CalendarEvents.modeChange, [ViewMode.timepicker]);
				return this._drawTimepicker();
		}
	}
	private _initHandlers() {
		this._handlers = {
			onclick: {
				".dhx_calendar-year, .dhx_calendar-month, .dhx_calendar-day": (_e, vn) => {
					const date = vn.attrs._date;
					const oldDate = DateHelper.copy(this._getSelected());
					switch (this._currentViewMode) {
						case ViewMode.calendar:
							const mergedDate = this.config.timePicker
								? DateHelper.mergeHoursAndMinutes(date, this._getSelected() || this._currentDate)
								: date;
							if (!this.events.fire(CalendarEvents.beforeChange, [mergedDate, oldDate, true])) {
								return;
							}
							if (this.config.range && this._selected.length === 1 && this._selected[0] < mergedDate) {
								this._selected.push(mergedDate);
							} else {
								this._selected = [];
								this._selected[0] = mergedDate;
							}
							this.showDate(this._getSelected());
							this.events.fire(CalendarEvents.change, [date, oldDate, true]);
							break;
						case ViewMode.months:
							if (this.config.view !== ViewMode.months) {
								DateHelper.setMonth(this._currentDate, date);
								this.showDate(null, ViewMode.calendar);
								this.events.fire(CalendarEvents.monthSelected, [date]);
							} else {
								const newDate = DateHelper.fromYearAndMonth(this._currentDate.getFullYear() || this._getSelected().getFullYear(), date);
								if (!this.events.fire(CalendarEvents.beforeChange, [newDate, oldDate, true])) {
									return;
								}
								this._currentDate = newDate;
								this._selected[0] = newDate;
								this.events.fire(CalendarEvents.change, [this._getSelected(), oldDate, true]);
								this.events.fire(CalendarEvents.monthSelected, [date]);
								this.paint();
							}
							break;
						case ViewMode.years:
							if (this.config.view !== ViewMode.years) {
								DateHelper.setYear(this._currentDate, date);
								this.showDate(null, ViewMode.months);
								this.events.fire(CalendarEvents.yearSelected, [date]);
							} else {
								const newDate = DateHelper.fromYear(date);
								if (!this.events.fire(CalendarEvents.beforeChange, [newDate, oldDate, true])) {
									return;
								}
								this._currentDate = newDate;
								this._selected[0] = newDate;
								this.events.fire(CalendarEvents.change, [this._getSelected(), oldDate, true]);
								this.events.fire(CalendarEvents.yearSelected, [date]);
								this.paint();
							}
					}
				},
				".dhx_calendar-action__cancel": () => {
					this.showDate(this._getSelected(), ViewMode.calendar);
					this.events.fire(CalendarEvents.cancelClick, []);
				},
				".dhx_calendar-action__show-month": () => this.showDate(null, ViewMode.months),
				".dhx_calendar-action__show-year": () => this.showDate(null, ViewMode.years),
				".dhx_calendar-action__next": () => {
					let newDate;
					switch (this._currentViewMode) {
						case ViewMode.calendar:
							newDate = DateHelper.addMonth(this._currentDate, 1);
							break;
						case ViewMode.months:
							newDate = DateHelper.addYear(this._currentDate, 1);
							break;
						case ViewMode.years:
							newDate = DateHelper.addYear(this._currentDate, 12);
					}
					this.showDate(newDate);
				},
				".dhx_calendar-action__prev": () => {
					let newDate;
					switch (this._currentViewMode) {
						case ViewMode.calendar:
							newDate = DateHelper.addMonth(this._currentDate, - 1);
							break;
						case ViewMode.months:
							newDate = DateHelper.addYear(this._currentDate, - 1);
							break;
						case ViewMode.years:
							newDate = DateHelper.addYear(this._currentDate, - 12);
					}
					this.showDate(newDate);
				},
				".dhx_calendar-action__show-timepicker": () => {
					this._currentViewMode = ViewMode.timepicker;
					this.paint();
				}
			},
			onmouseover: {
				".dhx_calendar-day": (e, vn) => this.events.fire(CalendarEvents.dateHover, [new Date(vn.attrs._date), e])
			}
		};
	}
	private _getData(date: Date): ICalendarWeek[] {
		const firstDay = this.config.weekStart === "monday" ? 1 : 0;
		const first = DateHelper.weekStart(DateHelper.monthStart(date), firstDay);
		const data = [];

		let weeksCount = 6;
		let currentDate = first;

		while (weeksCount--) {
			const currentWeek = DateHelper.getWeekNumber(currentDate);
			let disabledDays = 0;
			let daysCount = 7;
			const days = [];

			while (daysCount--) {
				const isDateWeekEnd = DateHelper.isWeekEnd(currentDate);
				const isCurrentMonth = date.getMonth() === currentDate.getMonth();
				const isBlocked = this.config.block && this.config.block(currentDate);

				const css = [];

				if (this.config.range && this._selected[0] && this._selected[1]) {
					const rangeMark = () => {
						if (this._selected[0] && this._selected[1]) {
							const firstDate = DateHelper.dayStart(this._selected[0]);
							const lastDate = DateHelper.dayStart(this._selected[1]);
							return currentDate >= firstDate && currentDate <= lastDate && getRangeClass();
						}
					};
					const getRangeClass = () => {
						if (DateHelper.isSameDay(this._selected[0], this._selected[1])) {
							return null;
						}
						return "dhx_calendar-day--in-range";
					};
					this.config.$rangeMark = rangeMark;
				}
				if (isDateWeekEnd && isCurrentMonth) {
					css.push("dhx_calendar-day--weekend");
				}
				if (!isCurrentMonth) {
					if (this.config.thisMonthOnly) {
						disabledDays++;
						css.push("dhx_calendar-day--hidden");
					} else {
						css.push("dhx_calendar-day--muffled");
					}
				}
				if (this.config.mark) {
					const markedCss = this.config.mark(currentDate);
					if (markedCss) {
						css.push(markedCss);
					}
				}
				if (this.config.$rangeMark) {
					const rangeMark = this.config.$rangeMark(currentDate);
					if(rangeMark) {
						css.push(rangeMark);
					}
				}
				if (isBlocked) {
					if (isDateWeekEnd) {
						css.push("dhx_calendar-day--weekend-disabled");
					} else {
						css.push("dhx_calendar-day--disabled");
					}
				}
				this._selected.forEach((selected, index) => {
					if (selected && DateHelper.isSameDay(selected, currentDate)) {
						let dayCss = "dhx_calendar-day--selected";
						if (this.config.range) {
							dayCss += ` dhx_calendar-day--selected-${index === 0 ? "first " : "last"}`;
						}
						css.push(dayCss);
					}
				});
				days.push({
					date: currentDate,
					day: currentDate.getDate(),
					css: css.join(" ")
				});
				currentDate = DateHelper.addDay(currentDate);
			}
			data.push({
				weekNumber: currentWeek,
				days,
				disabledWeekNumber: disabledDays === 7
			});
		}
		return data;
	}
	private _drawCalendar() {
		const date = this._currentDate;
		const { weekStart, thisMonthOnly, css, timePicker, width } = this.config;

		const weekDays = weekStart === "monday"
			? [...locale.daysShort.slice(1), locale.daysShort[0]]
			: locale.daysShort;
		const weekDaysHeader = weekDays.map(day => el(".dhx_calendar-weekday", day));

		const data = this._getData(date);
		let content = [];
		const weekNumbers = [];
		let weekNumbersWrapper;
		for (const week of data) {
			const weekRow = week.days.map(item => el("div.dhx_calendar-day", {
				class: item.css,
				_date: item.date,
				tabIndex: 1,
			}, item.day));

			if (this.config.weekNumbers && !(week.disabledWeekNumber && thisMonthOnly)) {
				weekNumbers.push(el("div", {
					class: "dhx_calendar-week-number"
				}, week.weekNumber));
			}
			content = content.concat(weekRow);
		}
		if (this.config.weekNumbers) {
			weekNumbersWrapper = el(".dhx_calendar__week-numbers", weekNumbers);
		}
		const widgetClass = "dhx_calendar dhx_widget" +
			(css ? " " + css : "") +
			(timePicker ? " dhx_calendar--with_timepicker" : "") +
			(this.config.weekNumbers ? " dhx_calendar--with_week-numbers" : "");
		return el("div", {
			class: widgetClass,
			style: { width: this.config.weekNumbers ? `calc(${width} + 48px )` : width },
			...this._handlers
		}, [
		el(".dhx_calendar__wrapper", [
			this._drawHeader(el("button.dhx_calendar-action__show-month.dhx_button.dhx_button--view_link.dhx_button--size_small.dhx_button--color_secondary.dhx_button--circle",
				locale.months[date.getMonth()] + " " + date.getFullYear())
			),
			this.config.weekNumbers && el(".dhx_calendar__dates-wrapper", [
				el(".dhx_calendar__weekdays", weekDaysHeader),
				el(".dhx_calendar__days", content),
				weekNumbersWrapper
			]),
			!this.config.weekNumbers && el(".dhx_calendar__weekdays", weekDaysHeader),
			!this.config.weekNumbers && el(".dhx_calendar__days", content),
			timePicker ?
				el(".dhx_timepicker__actions", [
					el("button.dhx_calendar__timepicker-button." +
						"dhx_button.dhx_button--view_link.dhx_button--size_small.dhx_button--color_secondary.dhx_button--width_full.dhx_button--circle.dhx_calendar-action__show-timepicker", [
							el("span.dhx_button__icon.dxi.dxi-clock-outline"),
							el("span.dhx_button__text", this._time),
						])
				]) : null,
			])
		]);
	}
	private _drawMonthSelector() {
		const date = this._currentDate;
		const currentMonth = date.getMonth();
		const currentYear = this._getSelected() ? this._getSelected().getFullYear() : null;
		const { css, timePicker, weekNumbers, width, view } = this.config;
		const widgetClass = "dhx_calendar dhx_widget" +
			(css ? " " + css : "") +
			(timePicker ? " dhx_calendar--with_timepicker" : "") +
			(weekNumbers ? " dhx_calendar--with_week-numbers" : "");
		return el("div", {
			class: widgetClass,
			style: {
				width: weekNumbers ? `calc(${width} + 48px)` : width,
			},
			...this._handlers
		}, [
			el(".dhx_calendar__wrapper", [
				this._drawHeader(el("button.dhx_calendar-action__show-year.dhx_button.dhx_button--view_link.dhx_button--size_small.dhx_button--color_secondary.dhx_button--circle",
					date.getFullYear())),
				el(".dhx_calendar__months", locale.monthsShort.map((item, i) => el("div", {
					class: "dhx_calendar-month" + (currentMonth === i && currentYear === date.getFullYear() ? " dhx_calendar-month--selected" : ""),
					tabIndex: 1,
					_date: i
				}, item))),
				view !== ViewMode.months ? el(".dhx_calendar__actions", [
					el("button.dhx_button.dhx_button--color_primary.dhx_button--view_link.dhx_button--size_small.dhx_button--width_full.dhx_button--circle.dhx_calendar-action__cancel", locale.cancel)
				]) : null
			])
		]);
	}
	private _drawYearSelector() {
		const date = this._currentDate;
		const yearsDiapason = DateHelper.getTwelweYears(date);
		const { css, timePicker, weekNumbers, width, view } = this.config;
		const widgetClass = "dhx_calendar dhx_widget" +
			(css ? " " + css : "") +
			(timePicker ? " dhx_calendar--with_timepicker" : "") +
			(weekNumbers ? " dhx_calendar--with_week-numbers" : "");
		return el("div", {
			class: widgetClass,
			style: { width: weekNumbers ? `calc(${width} + 48px)` : width },
			...this._handlers
			}, [
			el(".dhx_calendar__wrapper", [
				this._drawHeader(el("button.dhx_button.dhx_button--view_link.dhx_button--size_small.dhx_button--color_secondary.dhx_button--circle",
					yearsDiapason[0] + "-" + yearsDiapason[yearsDiapason.length - 1])),
				el(".dhx_calendar__years", yearsDiapason.map(item => el("div", {
					class: "dhx_calendar-year" + (this._getSelected() && item === this._getSelected().getFullYear() ? " dhx_calendar-year--selected" : ""),
					_date: item,
					tabIndex: 1,
				}, item))),
				view !== ViewMode.years && view !== ViewMode.months ? el(".dhx_calendar__actions", [
					el("button.dhx_button.dhx_button--color_primary.dhx_button--view_link.dhx_button--size_small.dhx_button--width_full.dhx_button--circle.dhx_calendar-action__cancel", locale.cancel)
				]) : null
			])
		]);
	}
	private _drawHeader(actionContent) {
		return el(".dhx_calendar__navigation", [
			el("button.dhx_calendar-navigation__button.dhx_calendar-action__prev" + linkButtonClasses + ".dhx_button--icon.dhx_button--circle", [
				el(".dhx_button__icon.dxi.dxi-chevron-left")
			]),
			actionContent,
			el("button.dhx_calendar-navigation__button.dhx_calendar-action__next" + linkButtonClasses + ".dhx_button--icon.dhx_button--circle", [
				el(".dhx_button__icon.dxi.dxi-chevron-right")
			]),
		]);
	}
	private _drawTimepicker() {
		const { css, weekNumbers, width } = this.config;
		return el(".dhx_widget.dhx-calendar", {
			class: (css ? " " + css : ""),
			style: { width: weekNumbers ? `calc(${width} + 48px)` : width }
		}, [
			inject(this._timepicker.getRootView())
		]);
	}
}